import React, { useState, useMemo, useEffect } from 'react';
import { useApp } from '../context/AppContext';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { format, parseISO, startOfWeek, endOfWeek, eachDayOfInterval, subWeeks, addWeeks } from 'date-fns';
import { 
  Clock, DollarSign, Download, ChevronLeft, ChevronRight,
  Users, Calendar, TrendingUp, TrendingDown, Car, MapPin,
  AlertTriangle, CheckCircle, Filter, FileSpreadsheet, Edit2, X,
  Play, Square, Plus, History, Save, UserPlus, Trash2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './TimeCardReport.css';

function TimeCardReport() {
  const { state } = useApp();
  const { user } = useAuth();
  const [timeRecords, setTimeRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('week'); // 'week' | 'day'
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  
  // Manager control states
  const [showClockModal, setShowClockModal] = useState(null); // { type: 'in' | 'out', employee: emp }
  const [showEditModal, setShowEditModal] = useState(null); // time record to edit
  const [showAddModal, setShowAddModal] = useState(false);
  const [editReason, setEditReason] = useState('');
  const [editData, setEditData] = useState({});
  const [addData, setAddData] = useState({
    employeeId: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    clockInTime: '08:00',
    clockOutTime: '17:00'
  });
  const [saving, setSaving] = useState(false);

  const weekEnd = useMemo(() => endOfWeek(selectedWeek, { weekStartsOn: 1 }), [selectedWeek]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: selectedWeek, end: weekEnd }), [selectedWeek, weekEnd]);

  // Load time records for selected week
  useEffect(() => {
    const loadTimeRecords = async () => {
      setLoading(true);
      try {
        const startStr = format(selectedWeek, 'yyyy-MM-dd');
        const endStr = format(weekEnd, 'yyyy-MM-dd');
        
        const recordsRef = collection(db, 'timeRecords');
        const q = query(
          recordsRef,
          where('date', '>=', startStr),
          where('date', '<=', endStr)
        );
        
        const snapshot = await getDocs(q);
        const records = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setTimeRecords(records);
      } catch (error) {
        console.error('Error loading time records:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTimeRecords();
  }, [selectedWeek, weekEnd]);

  // Get employee hourly rates
  const employeeRates = useMemo(() => {
    const rates = {};
    state.employees.forEach(emp => {
      rates[emp.id] = {
        hourlyRate: emp.hourlyRate || 15,
        name: `${emp.firstName} ${emp.lastName}`,
        firstName: emp.firstName,
        lastName: emp.lastName
      };
    });
    return rates;
  }, [state.employees]);

  // Calculate profitability for jobs on a given date for a given route
  const calculateRouteProfitability = (date, routeJobs, teamIds) => {
    if (!routeJobs || routeJobs.length === 0) return null;

    // Get the ACTUAL team members assigned to THIS route's jobs (primary team)
    const actualTeamIds = [...new Set(routeJobs.flatMap(job => job.team || []))];
    
    // Get ALL helpers assigned to jobs in this route
    const allHelperIds = [...new Set(routeJobs.flatMap(job => job.helpers || []))];
    
    // Combined: everyone who worked this route (team + helpers, deduplicated)
    const allWorkerIds = [...new Set([...actualTeamIds, ...allHelperIds])];
    
    // Get time records for this date and ALL workers on this route (team + helpers)
    const dateRecords = timeRecords.filter(r => 
      r.date === date && allWorkerIds.includes(r.employeeId)
    );
    
    // Calculate total labor cost from time records
    let totalLaborCost = 0;
    let totalHoursWorked = 0;
    
    dateRecords.forEach(record => {
      if (record.hoursWorked) {
        const rate = record.clockInRate || employeeRates[record.employeeId]?.hourlyRate || 15;
        totalLaborCost += record.hoursWorked * rate;
        totalHoursWorked += record.hoursWorked;
      }
    });

    // Calculate revenue from jobs
    const totalRevenue = routeJobs.reduce((sum, job) => sum + (job.price || 0), 0);
    const totalJobHours = routeJobs.reduce((sum, job) => sum + (job.duration || 0), 0);

    // Estimate drive time cost (assume 15 mins between jobs on average)
    const driveTimeHours = (routeJobs.length - 1) * 0.25; // 15 mins per transition
    const avgHourlyRate = allWorkerIds.length > 0 
      ? allWorkerIds.reduce((sum, id) => sum + (employeeRates[id]?.hourlyRate || 15), 0) / allWorkerIds.length 
      : 15;
    const driveTimeCost = driveTimeHours * avgHourlyRate * actualTeamIds.length;

    // If no time records exist but we have team assigned, estimate labor from job durations
    // This handles future dates and days where employees haven't clocked in yet
    let estimatedLaborCost = 0;
    const hasTimeRecords = totalHoursWorked > 0;
    if (!hasTimeRecords && allWorkerIds.length > 0) {
      // Estimate: (job hours + drive time) × avg rate × team size (incl helpers)
      estimatedLaborCost = (totalJobHours + driveTimeHours) * avgHourlyRate * allWorkerIds.length;
      totalLaborCost = estimatedLaborCost;
      totalHoursWorked = (totalJobHours + driveTimeHours) * allWorkerIds.length;
    }

    // Downtime = total hours worked - job hours - drive time
    const productiveHours = totalJobHours + driveTimeHours;
    const downtimeHours = hasTimeRecords ? Math.max(0, totalHoursWorked - productiveHours) : 0;
    const downtimeCost = downtimeHours * avgHourlyRate * allWorkerIds.length;

    // Calculate per-job profitability INCLUDING helpers per job
    const jobProfitability = routeJobs.map(job => {
      const jobTeam = job.team || [];
      const jobHelpers = job.helpers || [];
      const jobWorkerCount = new Set([...jobTeam, ...jobHelpers]).size;
      const jobHours = job.duration || 0;
      const actualHours = job.actual_duration_min ? job.actual_duration_min / 60 : jobHours;
      const laborCost = actualHours * avgHourlyRate * jobWorkerCount;
      const profit = (job.price || 0) - laborCost;
      const margin = job.price > 0 ? (profit / job.price) * 100 : 0;
      const profitPerPerson = jobWorkerCount > 0 ? profit / jobWorkerCount : profit;
      
      return {
        ...job,
        estimatedHours: jobHours,
        actualHours,
        laborCost,
        profit,
        margin,
        efficiency: jobHours > 0 ? (jobHours / actualHours) * 100 : 100,
        workerCount: jobWorkerCount,
        helperCount: jobHelpers.length,
        profitPerPerson
      };
    });

    const totalProfit = totalRevenue - totalLaborCost;
    const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;
    
    // Total people on this route (team + unique helpers)
    const totalPeopleCount = allWorkerIds.length;
    const profitPerPerson = totalPeopleCount > 0 ? totalProfit / totalPeopleCount : totalProfit;

    return {
      revenue: totalRevenue,
      laborCost: totalLaborCost,
      driveTimeCost,
      downtimeCost,
      profit: totalProfit,
      profitMargin,
      hoursWorked: totalHoursWorked,
      jobHours: totalJobHours,
      driveTimeHours,
      downtimeHours,
      jobProfitability,
      teamSize: actualTeamIds.length,
      helperCount: allHelperIds.length,
      totalPeopleCount,
      profitPerPerson,
      isEstimated: !hasTimeRecords && allWorkerIds.length > 0
    };
  };

  // Group time records by employee
  const timeRecordsByEmployee = useMemo(() => {
    const grouped = {};
    
    timeRecords.forEach(record => {
      if (!grouped[record.employeeId]) {
        grouped[record.employeeId] = {
          employeeId: record.employeeId,
          employeeName: record.employeeName || employeeRates[record.employeeId]?.name || 'Unknown',
          records: [],
          totalHours: 0,
          totalDays: 0,
          totalLaborCost: 0
        };
      }
      
      grouped[record.employeeId].records.push(record);
      if (record.hoursWorked) {
        grouped[record.employeeId].totalHours += record.hoursWorked;
        grouped[record.employeeId].totalDays += 1;
        const rate = record.clockInRate || employeeRates[record.employeeId]?.hourlyRate || 15;
        grouped[record.employeeId].totalLaborCost += record.hoursWorked * rate;
      }
    });

    return Object.values(grouped).sort((a, b) => b.totalHours - a.totalHours);
  }, [timeRecords, employeeRates]);

  // Get jobs for the week grouped by date and route
  const weeklyJobData = useMemo(() => {
    const data = {};
    
    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayJobs = state.jobs.filter(j => j.date === dateStr);
      
      // Group by route — smart fallback: prefer routeId, else derive from team composition
      const byRoute = {};
      dayJobs.forEach(job => {
        // Determine the grouping key:
        // 1. Use routeId if the job has one (set by RouteBuilder or Dispatch assign)
        // 2. Otherwise, derive from sorted team IDs (so same team = same group)
        // 3. Fall back to 'unassigned' if no team
        const teamIds = job.team || [];
        let routeId;
        if (job.routeId) {
          routeId = job.routeId;
        } else if (teamIds.length > 0) {
          routeId = `team_${[...teamIds].sort().join('_')}`;
        } else {
          routeId = 'unassigned';
        }

        if (!byRoute[routeId]) {
          byRoute[routeId] = {
            jobs: [],
            team: [],
            routeName: job.routeName || null
          };
        }
        byRoute[routeId].jobs.push(job);

        // Accumulate all team member IDs across jobs in this route
        if (teamIds.length > 0) {
          byRoute[routeId].team = [...new Set([...byRoute[routeId].team, ...teamIds])];
        }

        // Use the first non-null routeName we find, or build one from teamNames
        if (!byRoute[routeId].routeName) {
          if (job.routeName) {
            byRoute[routeId].routeName = job.routeName;
          } else if (job.teamNames && job.teamNames.length > 0) {
            byRoute[routeId].routeName = job.teamNames.map(n => n.split(' ')[0]).filter(Boolean).join(' & ');
          }
        }
      });
      
      data[dateStr] = byRoute;
    });
    
    return data;
  }, [weekDays, state.jobs]);

  // Calculate weekly summary
  const weeklySummary = useMemo(() => {
    let totalHours = 0;
    let totalLaborCost = 0;
    let totalRevenue = 0;
    
    timeRecordsByEmployee.forEach(emp => {
      totalHours += emp.totalHours;
      totalLaborCost += emp.totalLaborCost;
    });

    weekDays.forEach(day => {
      const dateStr = format(day, 'yyyy-MM-dd');
      const dayJobs = state.jobs.filter(j => j.date === dateStr);
      totalRevenue += dayJobs.reduce((sum, j) => sum + (j.price || 0), 0);
    });

    return {
      totalHours: Math.round(totalHours * 100) / 100,
      totalLaborCost: Math.round(totalLaborCost * 100) / 100,
      totalRevenue,
      profit: totalRevenue - totalLaborCost,
      profitMargin: totalRevenue > 0 ? ((totalRevenue - totalLaborCost) / totalRevenue * 100).toFixed(1) : 0,
      avgHoursPerDay: totalHours / 7,
      employeeCount: timeRecordsByEmployee.length
    };
  }, [timeRecordsByEmployee, weekDays, state.jobs]);

  // Export to CSV
  const exportToCSV = () => {
    const rows = [
      ['Time Card Report', `Week of ${format(selectedWeek, 'MMM d, yyyy')}`],
      [],
      ['Employee', 'Date', 'Role', 'Clock In', 'Clock Out', 'Hours Worked', 'Hourly Rate', 'Total Pay', 'Approval', 'Clock In Location', 'Clock Out Location'],
    ];

    timeRecordsByEmployee.forEach(emp => {
      emp.records.forEach(record => {
        const rate = record.clockInRate || employeeRates[record.employeeId]?.hourlyRate || 15;
        rows.push([
          emp.employeeName,
          record.date,
          record.clockInRole || '',
          record.clockInTime ? format(new Date(record.clockInTime), 'h:mm a') : '',
          record.clockOutTime ? format(new Date(record.clockOutTime), 'h:mm a') : '',
          record.hoursWorked?.toFixed(2) || '',
          `$${rate}`,
          record.hoursWorked ? `$${(record.hoursWorked * rate).toFixed(2)}` : '',
          record.approvalStatus || 'pending',
          record.clockInLocation ? `${record.clockInLocation.lat?.toFixed(4)}, ${record.clockInLocation.lng?.toFixed(4)}` : '',
          record.clockOutLocation ? `${record.clockOutLocation.lat?.toFixed(4)}, ${record.clockOutLocation.lng?.toFixed(4)}` : ''
        ]);
      });
      // Add employee total
      rows.push([
        `${emp.employeeName} TOTAL`,
        '',
        '',
        '',
        emp.totalHours.toFixed(2),
        '',
        `$${emp.totalLaborCost.toFixed(2)}`,
        '',
        ''
      ]);
      rows.push([]);
    });

    // Weekly summary
    rows.push([]);
    rows.push(['WEEKLY SUMMARY']);
    rows.push(['Total Hours', weeklySummary.totalHours]);
    rows.push(['Total Labor Cost', `$${weeklySummary.totalLaborCost.toFixed(2)}`]);
    rows.push(['Total Revenue', `$${weeklySummary.totalRevenue}`]);
    rows.push(['Gross Profit', `$${weeklySummary.profit.toFixed(2)}`]);
    rows.push(['Profit Margin', `${weeklySummary.profitMargin}%`]);

    const csvContent = rows.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `time-cards-${format(selectedWeek, 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return format(new Date(isoString), 'h:mm a');
  };

  // Reload time records
  const reloadRecords = async () => {
    setLoading(true);
    try {
      const startStr = format(selectedWeek, 'yyyy-MM-dd');
      const endStr = format(weekEnd, 'yyyy-MM-dd');
      
      const recordsRef = collection(db, 'timeRecords');
      const q = query(
        recordsRef,
        where('date', '>=', startStr),
        where('date', '<=', endStr)
      );
      
      const snapshot = await getDocs(q);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setTimeRecords(records);
    } catch (error) {
      console.error('Error loading time records:', error);
    } finally {
      setLoading(false);
    }
  };

  // Manager clock in employee
  const handleManagerClockIn = async (employee) => {
    setSaving(true);
    try {
      const now = new Date();
      const dateStr = format(now, 'yyyy-MM-dd');
      const docId = `${employee.id}_${dateStr}`;
      
      const timeRecord = {
        employeeId: employee.id,
        employeeName: `${employee.firstName} ${employee.lastName}`,
        date: dateStr,
        clockInTime: now.toISOString(),
        clockInLocation: null, // Manager clock-in doesn't have GPS
        status: 'clocked_in',
        managerClockIn: true,
        managerClockInBy: user?.email || 'Manager',
        managerClockInAt: now.toISOString(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, 'timeRecords', docId), timeRecord);
      await reloadRecords();
      setShowClockModal(null);
      alert(`${employee.firstName} clocked in successfully!`);
    } catch (error) {
      console.error('Error clocking in:', error);
      alert('Failed to clock in employee.');
    } finally {
      setSaving(false);
    }
  };

  // Manager clock out employee
  const handleManagerClockOut = async (record) => {
    setSaving(true);
    try {
      const now = new Date();
      const clockInTime = new Date(record.clockInTime);
      const hoursWorked = (now - clockInTime) / (1000 * 60 * 60);
      
      await updateDoc(doc(db, 'timeRecords', record.id), {
        clockOutTime: now.toISOString(),
        clockOutLocation: null,
        hoursWorked: Math.round(hoursWorked * 100) / 100,
        status: 'clocked_out',
        managerClockOut: true,
        managerClockOutBy: user?.email || 'Manager',
        managerClockOutAt: now.toISOString(),
        updatedAt: serverTimestamp()
      });
      
      await reloadRecords();
      setShowClockModal(null);
      alert('Employee clocked out successfully!');
    } catch (error) {
      console.error('Error clocking out:', error);
      alert('Failed to clock out employee.');
    } finally {
      setSaving(false);
    }
  };

  // Edit time record
  const handleEditTimeRecord = async () => {
    if (!editReason.trim()) {
      alert('Please provide a reason for the edit.');
      return;
    }
    
    setSaving(true);
    try {
      const record = showEditModal;
      const clockIn = new Date(`${record.date}T${editData.clockInTime}`);
      const clockOut = editData.clockOutTime ? new Date(`${record.date}T${editData.clockOutTime}`) : null;
      const hoursWorked = clockOut ? (clockOut - clockIn) / (1000 * 60 * 60) : null;
      
      // Create edit history entry
      const editHistory = record.editHistory || [];
      editHistory.push({
        editedAt: new Date().toISOString(),
        editedBy: user?.email || 'Manager',
        reason: editReason,
        previousClockIn: record.clockInTime ?? null,
        previousClockOut: record.clockOutTime ?? null,
        previousHoursWorked: record.hoursWorked ?? null,
        newClockIn: clockIn.toISOString(),
        newClockOut: clockOut?.toISOString() || null,
        newHoursWorked: hoursWorked
      });
      
      await updateDoc(doc(db, 'timeRecords', record.id), {
        clockInTime: clockIn.toISOString(),
        clockOutTime: clockOut?.toISOString() || null,
        hoursWorked: hoursWorked ? Math.round(hoursWorked * 100) / 100 : null,
        status: clockOut ? 'clocked_out' : 'clocked_in',
        editHistory,
        lastEditedBy: user?.email || 'Manager',
        lastEditedAt: new Date().toISOString(),
        updatedAt: serverTimestamp()
      });
      
      await reloadRecords();
      setShowEditModal(null);
      setEditReason('');
      setEditData({});
      alert('Time record updated successfully!');
    } catch (error) {
      console.error('Error editing time record:', error);
      alert('Failed to update time record.');
    } finally {
      setSaving(false);
    }
  };

  // Add new time record
  const handleAddTimeRecord = async () => {
    if (!addData.employeeId) {
      alert('Please select an employee.');
      return;
    }
    
    setSaving(true);
    try {
      const employee = state.employees.find(e => e.id === addData.employeeId);
      const docId = `${addData.employeeId}_${addData.date}`;
      const clockIn = new Date(`${addData.date}T${addData.clockInTime}`);
      const clockOut = addData.clockOutTime ? new Date(`${addData.date}T${addData.clockOutTime}`) : null;
      const hoursWorked = clockOut ? (clockOut - clockIn) / (1000 * 60 * 60) : null;
      
      const timeRecord = {
        employeeId: addData.employeeId,
        employeeName: employee ? `${employee.firstName} ${employee.lastName}` : 'Unknown',
        date: addData.date,
        clockInTime: clockIn.toISOString(),
        clockOutTime: clockOut?.toISOString() || null,
        hoursWorked: hoursWorked ? Math.round(hoursWorked * 100) / 100 : null,
        status: clockOut ? 'clocked_out' : 'clocked_in',
        manualEntry: true,
        createdBy: user?.email || 'Manager',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(doc(db, 'timeRecords', docId), timeRecord);
      await reloadRecords();
      setShowAddModal(false);
      setAddData({
        employeeId: '',
        date: format(new Date(), 'yyyy-MM-dd'),
        clockInTime: '08:00',
        clockOutTime: '17:00'
      });
      alert('Time record added successfully!');
    } catch (error) {
      console.error('Error adding time record:', error);
      alert('Failed to add time record.');
    } finally {
      setSaving(false);
    }
  };

  // Approve a clock-in
  const handleApproveClockIn = async (record) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'timeRecords', record.id), {
        approvalStatus: 'approved',
        approvedBy: user?.email || 'Manager',
        approvedAt: new Date().toISOString(),
        updatedAt: serverTimestamp()
      });
      await reloadRecords();
    } catch (error) {
      console.error('Error approving clock-in:', error);
      alert('Failed to approve clock-in.');
    } finally {
      setSaving(false);
    }
  };

  // Reject a clock-in
  const handleRejectClockIn = async (record, reason) => {
    setSaving(true);
    try {
      await updateDoc(doc(db, 'timeRecords', record.id), {
        approvalStatus: 'rejected',
        rejectedBy: user?.email || 'Manager',
        rejectedAt: new Date().toISOString(),
        rejectionReason: reason || 'No reason provided',
        updatedAt: serverTimestamp()
      });
      await reloadRecords();
    } catch (error) {
      console.error('Error rejecting clock-in:', error);
      alert('Failed to reject clock-in.');
    } finally {
      setSaving(false);
    }
  };

  // Delete a time record (manager only)
  const handleDeleteTimeRecord = async (record) => {
    const employeeName = record.employeeName || employeeRates[record.employeeId]?.name || 'this employee';
    const dateLabel = record.date ? format(parseISO(record.date), 'EEE, MMM d, yyyy') : 'this date';
    const confirmed = window.confirm(
      `Delete the timecard entry for ${employeeName} on ${dateLabel}?\n\nThis cannot be undone.`
    );
    if (!confirmed) return;

    setSaving(true);
    try {
      await deleteDoc(doc(db, 'timeRecords', record.id));
      await reloadRecords();
    } catch (error) {
      console.error('Error deleting time record:', error);
      alert('Failed to delete time record.');
    } finally {
      setSaving(false);
    }
  };

  // Pending approval records
  const pendingApprovals = useMemo(() => {
    return timeRecords.filter(r => !r.approvalStatus || r.approvalStatus === 'pending');
  }, [timeRecords]);

  // Get active employees (for quick clock in)
  const activeEmployees = useMemo(() => {
    return state.employees.filter(e => e.status === 'active');
  }, [state.employees]);

  // Get currently clocked in employees
  const clockedInToday = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return timeRecords.filter(r => r.date === today && r.status === 'clocked_in');
  }, [timeRecords]);

  // Get records from past days that are still clocked in (missed clock outs)
  const missedClockOuts = useMemo(() => {
    const today = format(new Date(), 'yyyy-MM-dd');
    return timeRecords.filter(r => r.date < today && r.status === 'clocked_in');
  }, [timeRecords]);

  return (
    <div className="time-card-report">
      <div className="report-header">
        <div>
          <h1><Clock size={28} /> Time Card Report</h1>
          <p>Track employee hours, labor costs, and route profitability</p>
        </div>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            Add Time Entry
          </button>
          <button className="btn btn-primary" onClick={exportToCSV}>
            <Download size={18} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Quick Clock In/Out Panel */}
      <div className="quick-clock-panel">
        <h3><UserPlus size={18} /> Quick Clock In/Out</h3>
        <div className="quick-clock-grid">
          {activeEmployees.map(emp => {
            const todayRecord = timeRecords.find(r => 
              r.employeeId === emp.id && 
              r.date === format(new Date(), 'yyyy-MM-dd')
            );
            const isClockedIn = todayRecord?.status === 'clocked_in';
            
            return (
              <div key={emp.id} className={`quick-clock-card ${isClockedIn ? 'clocked-in' : ''}`}>
                <div className="emp-avatar">
                  {emp.firstName?.[0]}{emp.lastName?.[0]}
                </div>
                <div className="emp-info">
                  <span className="emp-name">{emp.firstName} {emp.lastName?.[0]}.</span>
                  {isClockedIn && todayRecord && (
                    <span className="clock-time">In: {formatTime(todayRecord.clockInTime)}</span>
                  )}
                </div>
                {isClockedIn ? (
                  <button 
                    className="clock-btn out"
                    onClick={() => setShowClockModal({ type: 'out', record: todayRecord, employee: emp })}
                  >
                    <Square size={14} /> Out
                  </button>
                ) : (
                  <button 
                    className="clock-btn in"
                    onClick={() => setShowClockModal({ type: 'in', employee: emp })}
                    disabled={todayRecord?.status === 'clocked_out'}
                    title={todayRecord?.status === 'clocked_out' ? 'Already clocked out today' : ''}
                  >
                    <Play size={14} /> In
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Missed Clock Outs Alert */}
      {missedClockOuts.length > 0 && (
        <div className="missed-clockouts-alert">
          <div className="alert-header">
            <AlertTriangle size={18} />
            <h3>Missed Clock Outs ({missedClockOuts.length})</h3>
          </div>
          <p>The following employees are still clocked in from past days and need clock out times:</p>
          <div className="missed-clockouts-list">
            {missedClockOuts.map(record => (
              <div key={record.id} className="missed-clockout-item">
                <div className="missed-info">
                  <span className="missed-name">
                    {record.employeeName || employeeRates[record.employeeId]?.name || 'Unknown'}
                  </span>
                  <span className="missed-date">
                    {format(parseISO(record.date), 'EEE, MMM d')} — In: {formatTime(record.clockInTime)}
                  </span>
                </div>
                <button
                  className="btn btn-warning btn-sm"
                  onClick={() => {
                    setShowEditModal(record);
                    setEditData({
                      clockInTime: record.clockInTime ? format(new Date(record.clockInTime), 'HH:mm') : '',
                      clockOutTime: ''
                    });
                    setEditReason('');
                  }}
                >
                  <Edit2 size={14} /> Add Clock Out
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Approvals Panel */}
      {pendingApprovals.length > 0 && (
        <div className="pending-approvals-panel" style={{
          background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '12px',
          padding: '16px 20px', marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <AlertTriangle size={18} style={{ color: '#f59e0b' }} />
            <h3 style={{ margin: 0, fontSize: '16px', color: '#92400e' }}>
              Pending Clock-In Approvals ({pendingApprovals.length})
            </h3>
          </div>
          <p style={{ fontSize: '13px', color: '#92400e', margin: '0 0 12px' }}>
            Review and approve employee clock-ins to confirm they are accurate.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {pendingApprovals.map(record => (
              <div key={record.id} style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px',
                background: 'white', borderRadius: '8px', border: '1px solid #fde68a'
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '50%', background: '#1a2634',
                  color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '13px', fontWeight: 600, flexShrink: 0
                }}>
                  {record.employeeName?.split(' ').map(n => n[0]).join('') || '?'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{record.employeeName}</div>
                  <div style={{ fontSize: '12px', color: '#6b7280' }}>
                    {format(parseISO(record.date), 'EEE, MMM d')} — In: {formatTime(record.clockInTime)}
                    {record.clockOutTime && ` — Out: ${formatTime(record.clockOutTime)}`}
                    {record.clockInRole && (
                      <span style={{
                        background: '#e0e7ff', color: '#3730a3', padding: '1px 6px',
                        borderRadius: '4px', marginLeft: '6px', fontSize: '11px', fontWeight: 600
                      }}>
                        {record.clockInRole} @ ${record.clockInRate?.toFixed(2)}/hr
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                  <button
                    onClick={() => handleApproveClockIn(record)}
                    disabled={saving}
                    style={{
                      background: '#10b981', color: 'white', border: 'none', borderRadius: '6px',
                      padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: '4px'
                    }}
                  >
                    <CheckCircle size={14} /> Approve
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('Reason for rejection (optional):');
                      if (reason !== null) handleRejectClockIn(record, reason);
                    }}
                    disabled={saving}
                    style={{
                      background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px',
                      padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Week Navigation */}
      <div className="week-nav">
        <button className="nav-btn" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))}>
          <ChevronLeft size={20} />
        </button>
        <div className="week-display">
          <Calendar size={18} />
          <span>
            {format(selectedWeek, 'MMM d')} - {format(weekEnd, 'MMM d, yyyy')}
          </span>
        </div>
        <button className="nav-btn" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))}>
          <ChevronRight size={20} />
        </button>
        <button 
          className="today-btn"
          onClick={() => setSelectedWeek(startOfWeek(new Date(), { weekStartsOn: 1 }))}
        >
          This Week
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="card-icon hours">
            <Clock size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">{weeklySummary.totalHours}h</span>
            <span className="card-label">Total Hours</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon labor">
            <Users size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">${weeklySummary.totalLaborCost.toFixed(0)}</span>
            <span className="card-label">Labor Cost</span>
          </div>
        </div>
        <div className="summary-card">
          <div className="card-icon revenue">
            <DollarSign size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">${weeklySummary.totalRevenue}</span>
            <span className="card-label">Revenue</span>
          </div>
        </div>
        <div className={`summary-card ${weeklySummary.profit >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-icon profit">
            {weeklySummary.profit >= 0 ? <TrendingUp size={24} /> : <TrendingDown size={24} />}
          </div>
          <div className="card-content">
            <span className="card-value">${weeklySummary.profit.toFixed(0)}</span>
            <span className="card-label">{weeklySummary.profitMargin}% Margin</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading time records...</div>
      ) : (
        <>
          {/* Employee Time Cards */}
          <div className="section">
            <h2><Users size={20} /> Employee Time Cards</h2>
            
            {timeRecordsByEmployee.length === 0 ? (
              <div className="empty-state">
                <Clock size={48} />
                <p>No time records for this week</p>
              </div>
            ) : (
              <div className="employee-cards">
                {timeRecordsByEmployee.map(emp => (
                  <div key={emp.employeeId} className="employee-card">
                    <div className="employee-header">
                      <div className="employee-avatar">
                        {emp.employeeName.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div className="employee-info">
                        <h3>{emp.employeeName}</h3>
                        <span className="rate">
                          ${employeeRates[emp.employeeId]?.hourlyRate || 15}/hr
                        </span>
                      </div>
                      <div className="employee-totals">
                        <span className="total-hours">{emp.totalHours.toFixed(1)}h</span>
                        <span className="total-pay">${emp.totalLaborCost.toFixed(2)}</span>
                      </div>
                    </div>
                    
                    <div className="time-entries">
                      {emp.records.map(record => (
                        <div key={record.id} className={`time-entry ${record.editHistory?.length > 0 ? 'edited' : ''}`}>
                          <div className="entry-date">
                            {format(parseISO(record.date), 'EEE, MMM d')}
                            {record.manualEntry && <span className="entry-badge manual">Manual</span>}
                            {record.managerClockIn && <span className="entry-badge manager">Mgr In</span>}
                            {record.managerClockOut && <span className="entry-badge manager">Mgr Out</span>}
                            {record.clockInRole && (
                              <span className="entry-badge" style={{ background: '#e0e7ff', color: '#3730a3' }}>
                                {record.clockInRole}
                              </span>
                            )}
                            {record.approvalStatus === 'approved' && (
                              <span className="entry-badge" style={{ background: '#d1fae5', color: '#065f46' }}>Approved</span>
                            )}
                            {record.approvalStatus === 'rejected' && (
                              <span className="entry-badge" style={{ background: '#fee2e2', color: '#991b1b' }}>Rejected</span>
                            )}
                            {(!record.approvalStatus || record.approvalStatus === 'pending') && (
                              <span className="entry-badge" style={{ background: '#fef3c7', color: '#92400e' }}>Pending</span>
                            )}
                          </div>
                          <div className="entry-times">
                            <span className="clock-in">
                              <CheckCircle size={12} />
                              {formatTime(record.clockInTime)}
                            </span>
                            <span className="clock-out">
                              {record.clockOutTime ? (
                                <>
                                  <CheckCircle size={12} />
                                  {formatTime(record.clockOutTime)}
                                </>
                              ) : record.date < format(new Date(), 'yyyy-MM-dd') ? (
                                <button
                                  className="missing-clockout-btn"
                                  onClick={() => {
                                    setShowEditModal(record);
                                    setEditData({
                                      clockInTime: record.clockInTime ? format(new Date(record.clockInTime), 'HH:mm') : '',
                                      clockOutTime: ''
                                    });
                                    setEditReason('');
                                  }}
                                >
                                  <AlertTriangle size={12} />
                                  Missing clock out — click to fix
                                </button>
                              ) : (
                                <>
                                  <AlertTriangle size={12} />
                                  Still clocked in
                                </>
                              )}
                            </span>
                          </div>
                          <div className="entry-hours">
                            {record.hoursWorked ? `${record.hoursWorked.toFixed(2)}h` : '-'}
                          </div>
                          <div className="entry-location">
                            {record.clockInLocation && (
                              <MapPin size={12} title={`${record.clockInLocation.lat?.toFixed(4)}, ${record.clockInLocation.lng?.toFixed(4)}`} />
                            )}
                          </div>
                          <div className="entry-actions">
                            {record.editHistory?.length > 0 && (
                              <button 
                                className="action-btn history"
                                title={`Edited ${record.editHistory.length} time(s). Last: ${record.editHistory[record.editHistory.length-1]?.reason}`}
                              >
                                <History size={14} />
                              </button>
                            )}
                            <button
                              className="action-btn edit"
                              onClick={() => {
                                setShowEditModal(record);
                                setEditData({
                                  clockInTime: record.clockInTime ? format(new Date(record.clockInTime), 'HH:mm') : '',
                                  clockOutTime: record.clockOutTime ? format(new Date(record.clockOutTime), 'HH:mm') : ''
                                });
                                setEditReason('');
                              }}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              className="action-btn delete"
                              title="Delete time entry"
                              disabled={saving}
                              onClick={() => handleDeleteTimeRecord(record)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Daily Route Profitability */}
          <div className="section">
            <h2><TrendingUp size={20} /> Daily Route Profitability</h2>
            
            <div className="profitability-grid">
              {weekDays.map(day => {
                const dateStr = format(day, 'yyyy-MM-dd');
                const dayData = weeklyJobData[dateStr] || {};
                const routes = Object.entries(dayData);
                const isToday = dateStr === format(new Date(), 'yyyy-MM-dd');
                
                return (
                  <div key={dateStr} className={`day-card ${isToday ? 'today' : ''}`}>
                    <div className="day-header">
                      <span className="day-name">{format(day, 'EEE')}</span>
                      <span className="day-date">{format(day, 'MMM d')}</span>
                    </div>
                    
                    {routes.length === 0 ? (
                      <div className="no-routes">No jobs</div>
                    ) : (
                      <div className="route-list">
                        {routes.map(([routeId, routeData]) => {
                          const profitability = calculateRouteProfitability(
                            dateStr, 
                            routeData.jobs, 
                            routeData.team
                          );
                          
                          if (!profitability) return null;
                          
                          // Get display name: use routeName if available, otherwise derive from team names
                          const teamMemberNames = [...new Set(routeData.jobs.flatMap(job => job.team || []))]
                            .map(empId => employeeRates[empId]?.name || empId);
                          
                          // Also get helper names
                          const helperMemberNames = [...new Set(routeData.jobs.flatMap(job => job.helpers || []))]
                            .filter(id => !routeData.jobs.some(job => (job.team || []).includes(id)))
                            .map(empId => employeeRates[empId]?.name || empId);
                          
                          const displayName = routeId === 'unassigned' 
                            ? 'Unassigned' 
                            : (routeData.routeName || teamMemberNames.map(n => n.split(' ')[0]).join(' & ') || `Route ${routeId.slice(-1).toUpperCase()}`);
                          
                          // Get team member names for display
                          const teamNames = teamMemberNames.length > 0 
                            ? teamMemberNames.join(', ')
                            : 'No team assigned';
                          
                          return (
                            <div key={routeId} className="route-profit-card">
                              <div className="route-name">
                                {displayName}
                              </div>
                              <div className="route-team-info" title={teamNames}>
                                {teamNames}
                                {helperMemberNames.length > 0 && (
                                  <span className="helper-tag"> +{helperMemberNames.length} helper{helperMemberNames.length > 1 ? 's' : ''}</span>
                                )}
                              </div>
                              <div className="profit-details">
                                <div className="profit-row">
                                  <span>Revenue:</span>
                                  <span className="value">${profitability.revenue}</span>
                                </div>
                                <div className="profit-row">
                                  <span>Labor:{profitability.isEstimated ? ' ~' : ''}</span>
                                  <span className="value cost">-${profitability.laborCost.toFixed(0)}</span>
                                </div>
                                <div className="profit-row">
                                  <span>Drive Time:</span>
                                  <span className="value cost">-${profitability.driveTimeCost.toFixed(0)}</span>
                                </div>
                                <div className={`profit-row total ${profitability.profit >= 0 ? 'positive' : 'negative'}`}>
                                  <span>Profit:</span>
                                  <span className="value">${profitability.profit.toFixed(0)}</span>
                                </div>
                                {profitability.totalPeopleCount > 0 && (
                                  <div className={`profit-row per-person ${profitability.profitPerPerson >= 0 ? 'positive' : 'negative'}`}>
                                    <span>
                                      <Users size={10} style={{verticalAlign: 'middle', marginRight: '3px'}} />
                                      Per Person ({profitability.totalPeopleCount}):
                                    </span>
                                    <span className="value">${profitability.profitPerPerson.toFixed(0)}</span>
                                  </div>
                                )}
                                <div className="margin-bar">
                                  <div 
                                    className={`margin-fill ${profitability.profitMargin >= 40 ? 'good' : profitability.profitMargin >= 20 ? 'ok' : 'poor'}`}
                                    style={{ width: `${Math.max(0, Math.min(100, profitability.profitMargin))}%` }}
                                  />
                                </div>
                                <span className="margin-text">{profitability.profitMargin.toFixed(0)}% margin</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Clock In/Out Modal */}
      {showClockModal && (
        <div className="modal-overlay" onClick={() => setShowClockModal(null)}>
          <div className="modal-content clock-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showClockModal.type === 'in' ? 'Clock In Employee' : 'Clock Out Employee'}</h2>
              <button className="close-btn" onClick={() => setShowClockModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="clock-employee-info">
                <div className="emp-avatar large">
                  {showClockModal.employee?.firstName?.[0]}{showClockModal.employee?.lastName?.[0]}
                </div>
                <h3>{showClockModal.employee?.firstName} {showClockModal.employee?.lastName}</h3>
              </div>
              
              {showClockModal.type === 'out' && showClockModal.record && (
                <div className="clock-summary">
                  <p>Clocked in at: <strong>{formatTime(showClockModal.record.clockInTime)}</strong></p>
                  <p>Time worked: <strong>
                    {((new Date() - new Date(showClockModal.record.clockInTime)) / (1000 * 60 * 60)).toFixed(2)} hours
                  </strong></p>
                </div>
              )}
              
              <p className="clock-warning">
                <AlertTriangle size={16} />
                This action will be logged with your email address.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowClockModal(null)}>
                Cancel
              </button>
              <button 
                className={`btn ${showClockModal.type === 'in' ? 'btn-primary' : 'btn-warning'}`}
                onClick={() => {
                  if (showClockModal.type === 'in') {
                    handleManagerClockIn(showClockModal.employee);
                  } else {
                    handleManagerClockOut(showClockModal.record);
                  }
                }}
                disabled={saving}
              >
                {saving ? 'Processing...' : showClockModal.type === 'in' ? 'Clock In' : 'Clock Out'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Time Record Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(null)}>
          <div className="modal-content edit-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Time Record</h2>
              <button className="close-btn" onClick={() => setShowEditModal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="edit-info">
                <p><strong>{showEditModal.employeeName}</strong> - {format(parseISO(showEditModal.date), 'EEEE, MMMM d, yyyy')}</p>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Clock In Time</label>
                  <input 
                    type="time"
                    value={editData.clockInTime || ''}
                    onChange={e => setEditData({ ...editData, clockInTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Clock Out Time</label>
                  <input 
                    type="time"
                    value={editData.clockOutTime || ''}
                    onChange={e => setEditData({ ...editData, clockOutTime: e.target.value })}
                  />
                </div>
              </div>
              
              {editData.clockInTime && editData.clockOutTime && (
                <div className="hours-preview">
                  Calculated hours: <strong>
                    {(() => {
                      const start = new Date(`2000-01-01T${editData.clockInTime}`);
                      const end = new Date(`2000-01-01T${editData.clockOutTime}`);
                      return ((end - start) / (1000 * 60 * 60)).toFixed(2);
                    })()}h
                  </strong>
                </div>
              )}
              
              <div className="form-group">
                <label>Reason for Edit *</label>
                <textarea
                  value={editReason}
                  onChange={e => setEditReason(e.target.value)}
                  placeholder="e.g., Employee forgot to clock out, correcting entry error..."
                  rows={3}
                />
              </div>
              
              {showEditModal.editHistory?.length > 0 && (
                <div className="edit-history">
                  <h4><History size={14} /> Previous Edits</h4>
                  {showEditModal.editHistory.map((edit, idx) => (
                    <div key={idx} className="history-entry">
                      <span className="history-date">{format(new Date(edit.editedAt), 'MMM d, h:mm a')}</span>
                      <span className="history-by">by {edit.editedBy}</span>
                      <span className="history-reason">{edit.reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowEditModal(null)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleEditTimeRecord}
                disabled={saving || !editReason.trim()}
              >
                <Save size={18} />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Time Record Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content add-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Time Entry</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Employee *</label>
                <select
                  value={addData.employeeId}
                  onChange={e => setAddData({ ...addData, employeeId: e.target.value })}
                >
                  <option value="">Select employee...</option>
                  {activeEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.firstName} {emp.lastName}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label>Date *</label>
                <input 
                  type="date"
                  value={addData.date}
                  onChange={e => setAddData({ ...addData, date: e.target.value })}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Clock In Time</label>
                  <input 
                    type="time"
                    value={addData.clockInTime}
                    onChange={e => setAddData({ ...addData, clockInTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Clock Out Time</label>
                  <input 
                    type="time"
                    value={addData.clockOutTime}
                    onChange={e => setAddData({ ...addData, clockOutTime: e.target.value })}
                  />
                </div>
              </div>
              
              {addData.clockInTime && addData.clockOutTime && (
                <div className="hours-preview">
                  Calculated hours: <strong>
                    {(() => {
                      const start = new Date(`2000-01-01T${addData.clockInTime}`);
                      const end = new Date(`2000-01-01T${addData.clockOutTime}`);
                      return ((end - start) / (1000 * 60 * 60)).toFixed(2);
                    })()}h
                  </strong>
                </div>
              )}
              
              <p className="manual-note">
                <AlertTriangle size={14} />
                This entry will be marked as a manual entry created by {user?.email || 'manager'}.
              </p>
            </div>
            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleAddTimeRecord}
                disabled={saving || !addData.employeeId}
              >
                <Plus size={18} />
                {saving ? 'Adding...' : 'Add Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TimeCardReport;
